/**
 * ``features/dxf/utils`` — shared formatters + status vocabulary.
 *
 * Extracted from the 3 row/detail/stage components (each previously
 * carried its own copy of ``formatBytes`` / ``extOf`` / ``STATUS_META``
 * — drift waiting to happen). Single source of truth keeps the
 * Italian status copy + the semantic color tokens + the byte-formatting
 * rules consistent across the entire CAD library surface.
 *
 * No external deps — pure functions + a frozen lookup. Safe to import
 * from server components if/when we move to RSC.
 */

import type { I18n, MessageDescriptor } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import type { DxfFile } from "@ando/core";


// ---------------------------------------------------------------------------
// File-shape formatters
// ---------------------------------------------------------------------------


/**
 * Format a byte count as ``"<n.n> <unit>"`` (B / KB / MB / GB). Returns
 * an em-dash when the input is missing or non-finite — the chip stays
 * stable instead of flashing "NaN B" while the BE-rendered row hydrates.
 */
export function formatBytes(bytes: number | undefined | null): string {
  if (!bytes || !Number.isFinite(bytes) || bytes <= 0) return "—";
  const k = 1024;
  const units = ["B", "KB", "MB", "GB"] as const;
  const i = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(k)));
  return `${(bytes / k ** i).toFixed(1)} ${units[i]}`;
}


/**
 * Extract the uppercase filename extension (``"DXF"`` / ``"DWG"`` /
 * ``"STEP"``). Returns ``"—"`` when there is no dot in the filename.
 */
export function extOf(filename: string): string {
  const dot = filename.lastIndexOf(".");
  return dot < 0 ? "—" : filename.slice(dot + 1).toUpperCase();
}


// ---------------------------------------------------------------------------
// CAD format → subtab bucketing + library badge
// ---------------------------------------------------------------------------


/**
 * The ``cad_format`` discriminators the library understands. Mechanical
 * axis: ``dxf`` / ``dwg`` (2D) + ``step`` / ``iges`` / ``stl`` (3D).
 * Fashion axis (FashionSigLIP): ``fashion-dxf`` (2D apparel pattern) +
 * ``fashion-3d`` (3D garment mesh) + ``fashion-project`` (CLO/Marvelous
 * project container). Downstream code treats the resolved value as a loose
 * ``string`` — the BE owns the source of truth and may add values before
 * the FE knows about them, so resolvers degrade to the raw token instead
 * of throwing on a hard union.
 */
export type CadLibraryFormat =
  | "dxf"
  | "dwg"
  | "step"
  | "iges"
  | "stl"
  | "fashion-dxf"
  | "fashion-3d"
  | "fashion-project";


/**
 * Resolve the library format from the explicit ``cad_format`` column or,
 * when the analyzer hasn't tagged the row yet, the filename suffix. Single
 * source of truth shared by the subtab filter (``technical.tsx``) and the
 * badge / preview components so the 2D-vs-3D bucketing can never drift from
 * the label the user reads.
 */
export function resolveCadFormat(
  file: Pick<DxfFile, "cad_format" | "filename">,
): string {
  if (file.cad_format) return file.cad_format;
  const lower = (file.filename ?? "").toLowerCase();
  const dot = lower.lastIndexOf(".");
  if (dot < 0) return "dxf";
  const ext = lower.slice(dot + 1);
  if (ext === "stp") return "step";
  if (ext === "igs") return "iges";
  return ext;
}


/**
 * Fashion-axis formats belong to the FashionSigLIP garment pipeline, not
 * the mechanical CAD axis. They drive the accent badge AND the static-
 * montage preview routing: there is no interactive mesh viewer for
 * OBJ/glTF/GLB/FBX or the proprietary CLO container, so the BE-rendered
 * contact-sheet PNG is THE preview (feeding an ``.obj`` / ``.zprj`` into
 * the DXF/STEP/STL ``CadViewerSwitch`` would surface "formato non
 * riconosciuto").
 */
export function isFashionFormat(format: string | null | undefined): boolean {
  return typeof format === "string" && format.startsWith("fashion");
}


export interface CadFormatBadge {
  readonly label: string;
  /** Tailwind chip classes — the pencil accent recipe (orange-soft fill,
   *  orange ink) shared with the drawing-type + version chips. */
  readonly className: string;
}


/**
 * Friendly, natural-language labels for the fashion-axis formats. Keyed by
 * the resolved ``cad_format`` token. Localizable message descriptors —
 * resolved against the active locale by ``cadFormatBadgeFor``.
 */
const FASHION_FORMAT_LABEL: Record<string, MessageDescriptor> = Object.freeze({
  "fashion-3d": msg`Capo 3D`,
  "fashion-project": msg`Progetto moda`,
  "fashion-dxf": msg`Cartamodello`,
});


/**
 * Resolve the format badge for a library row. Returns ``null`` for the
 * mechanical formats (``dxf`` / ``dwg`` / ``step`` / ``iges`` / ``stl``) —
 * those already read their type from the uppercase extension in the meta
 * line, so they get NO extra chip and the row stays byte-identical to the
 * pre-fashion layout. Only the fashion-axis formats surface a friendly
 * accent badge, so a garment mesh never masquerades as a bare "OBJ" /
 * "ZPRJ" token.
 */
export function cadFormatBadgeFor(
  i18n: I18n,
  format: string | null | undefined,
): CadFormatBadge | null {
  const descriptor = FASHION_FORMAT_LABEL[format ?? ""];
  if (!descriptor) return null;
  return {
    label: i18n._(descriptor),
    className: "bg-ando-orange-soft text-ando-orange",
  };
}


// ---------------------------------------------------------------------------
// BE ingestion lifecycle → user-facing chip vocabulary
// ---------------------------------------------------------------------------


export interface DxfStatusMeta {
  readonly label: string;
  /**
   * Tailwind classes for the status chip background + text. Uses
   * semantic design tokens (``success``/``warning``/``error``) so
   * dark-mode + theme refactors propagate without per-component edits.
   * The previous per-component hex strings (``#D1FAE5`` etc.) caused
   * drift between the row and the detail pane — this fixes it.
   */
  readonly className: string;
}


interface DxfStatusMetaDescriptor {
  readonly label: MessageDescriptor;
  readonly className: string;
}


/**
 * Status vocabulary aligned with the BE ingestion lifecycle (see
 * ``dxf_library.py::list_dxf_files``). Frozen at module load so
 * accidental mutation surfaces as a runtime error. Labels are
 * localizable message descriptors — resolved against the active locale
 * by ``statusMetaFor`` (this is a non-component util, so the caller
 * passes its ``i18n`` from ``useLingui()``).
 */
const STATUS_META_TABLE: Record<string, DxfStatusMetaDescriptor> = Object.freeze({
  pending: {
    label: msg`In coda`,
    className: "bg-warning/15 text-warning",
  },
  processing: {
    label: msg`Elaborazione`,
    className: "bg-warning/15 text-warning",
  },
  completed: {
    label: msg`Indicizzato`,
    className: "bg-success/15 text-success",
  },
  failed: {
    label: msg`Errore`,
    className: "bg-error/15 text-error",
  },
});


const STATUS_FALLBACK_CLASS = "bg-ink-3/15 text-ink-2";


/**
 * Resolve the chip metadata for a BE-reported status. Falls back to a
 * neutral ink chip + the raw status string when the BE adds a new
 * value before the FE knows about it (defensive, prevents a blank chip).
 */
export function statusMetaFor(
  i18n: I18n,
  file: Pick<DxfFile, "status">,
): DxfStatusMeta {
  const known = STATUS_META_TABLE[file.status];
  if (known) {
    return { label: i18n._(known.label), className: known.className };
  }
  // Surface unknown status verbatim so ops sees it on the chip rather
  // than a silent fallback to "—".
  return { label: file.status || "—", className: STATUS_FALLBACK_CLASS };
}
